import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Job, useListJobs } from '../../../app/ApiGen';
import { RefreshButton } from '../../../components/DataTable/Toolbar';
import { DataErrorHandler } from '../../../components/ErrorHandler';
import { ExportCsv, ExportProps } from '../../../components/ExportCsv';
import { TableLink } from '../../../components/Link';
import {
  Card,
  ColumnChanger,
  ColumnChangerProps,
  DataTable,
  SearchField,
  SearchFieldProps,
  Workspace,
} from '../../../design-system';
import { ColumnDefinitions } from '../../../utils/dataTable/columns';
import { navigateOnRowClick } from '../../../utils/dataTable/rowHelpers';
import { getCondensedDate } from '../../../utils/dateFormat';
import { filterBySelectableItem } from '../../../utils/jobs/filterComponents/SelectableFilters';
import { YesNoNAFilters, YesNoNAOption } from '../../../utils/jobs/filterComponents/YesNoNAFilters';
import { jobColumns, JobListData, jobListOptions, toJobListData } from '../../../utils/jobs/table';
import { normalise, Normalised } from '../../../utils/request';

type InstallerJobData = JobListData;

function toInstallerJobData(jobs: Normalised<Job>[]): InstallerJobData[] {
  return jobs.map(toJobListData);
}

export const JobsList: React.FC = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { data, error, loading, refetch } = useListJobs({});

  if (error) {
    return <DataErrorHandler description="Unable to load jobs" error={error} refetch={refetch} />;
  }

  const tableData: InstallerJobData[] = data ? toInstallerJobData(normalise(data)) : [];
  const columns = jobColumns as ColumnDefinitions<InstallerJobData>;

  return (
    <Workspace maxWidth={false}>
      <Alert severity="info" sx={{ marginBottom: 2 }}>
        {`Third-party job creation has been moved to the Evnex Installation app. Download the app on `}
        <Link href="https://play.google.com/store/apps/details?id=com.evnexinstallationapp" target="_blank">
          Android
        </Link>
        {` or `}
        <Link href="https://apps.apple.com/nz/app/evnex-installation/id1471576322" target="_blank">
          iOS
        </Link>
        {` to create a job or commission one that has been created for you.`}
      </Alert>
      <Card>
        <DataTable<InstallerJobData>
          isLoading={loading}
          options={jobListOptions<InstallerJobData>()}
          toolbarProps={{
            search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
              <SearchField dataManager={dataManager} item="jobs" onSearchChanged={onSearchChanged} search={search} />
            ),
            actions: (props: ColumnChangerProps & ExportProps<InstallerJobData>) => (
              <>
                <ColumnChanger
                  columnsButton={props.columnsButton}
                  columns={props.columns}
                  icons={props.icons}
                  onColumnsChanged={props.onColumnsChanged}
                />
                <RefreshButton refetch={refetch} />
                <ExportCsv<InstallerJobData>
                  columns={props.columns}
                  data={props.data}
                  exportFileName={props.exportFileName}
                  getFieldValue={props.getFieldValue}
                  icons={props.icons}
                />
                <Button color="primary" disabled variant="contained">
                  Create job
                </Button>
              </>
            ),
          }}
          columns={[
            {
              field: 'created',
              title: 'Created',
              type: 'date',
              defaultSort: 'desc',
              render: ({ id, created }) => <TableLink to={`${url}/${id}`}>{getCondensedDate(created)}</TableLink>,
            },
            {
              field: 'updated',
              title: 'Last Updated',
              type: 'date',
              hidden: true,
              render: ({ updated }) => getCondensedDate(updated),
              width: '10%',
            },
            columns.referenceId,
            columns.type,
            columns.status,
            {
              field: 'cocCompleted',
              title: 'CoC Completed',
              filterComponent: YesNoNAFilters,
              customFilterAndSearch: (search: unknown, { cocCompleted }) =>
                filterBySelectableItem<YesNoNAOption>(search, { value: cocCompleted }),
            },
            columns.organisationName,
            columns.customerName,
            columns.email,
            columns.address,
            columns.icpNumber,
            columns.installerLabel,
          ]}
          data={tableData}
          onRowClick={navigateOnRowClick((rowData) => `${url}/${rowData.id}`, history)}
        />
      </Card>
    </Workspace>
  );
};
